class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.classList.remove('is-empty');
    if (parsedState?.id) this.productId = parsedState.id;

    const sections = parsedState?.sections;
    const drawerSection = this.getSectionContent(sections, 'cart-drawer');

    if (!drawerSection) {
      this.refreshDrawerFromCartPage();
      return;
    }

    this.applySections(sections);

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  refreshDrawerFromCartPage() {
    const cartUrl = window.routes?.cart_url || routes?.cart_url || '/cart';

    fetch(`${cartUrl}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceDrawer = parsed.querySelector('#CartDrawer');
        const targetDrawer = document.querySelector('#CartDrawer');

        if (sourceDrawer && targetDrawer) {
          targetDrawer.innerHTML = sourceDrawer.innerHTML;
          this.classList.remove('is-empty');
        }

        return fetch(`${cartUrl}?section_id=cart-icon-bubble`);
      })
      .then((response) => (response ? response.text() : null))
      .then((iconHtml) => {
        if (iconHtml) this.updateCartIconFromSection(iconHtml);

        setTimeout(() => {
          this.bindOverlay();
          this.open();
        });
      })
      .catch((error) => {
        console.error(error);
        setTimeout(() => this.open(), 0);
      });
  }

  updateCartIconFromSection(sectionHtml) {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink || !sectionHtml) return;

    const iconMarkup = this.getSectionInnerHTML(sectionHtml, '.shopify-section');
    if (iconMarkup) cartLink.innerHTML = iconMarkup;
  }

  applySections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = this.getSectionContent(sections, section.id);
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });
  }

  getSectionContent(sections, id) {
    if (!sections) return null;
    if (sections[id]) return sections[id];
    const match = Object.keys(sections).find((key) => key === id || key.endsWith(`__${id}`));
    return match ? sections[match] : null;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
